import { isNative, openExternal } from '../native';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { c } from '../theme';
import { Wordmark } from './Brand';
import { type User } from '../api';
import { useAuth } from '../auth';

export type MenuScreen = 'search' | 'portfolio' | 'report' | 'assembling' | 'account' | '';

type Item = {
  label: string;
  sub: string;
  to: string;
  key: MenuScreen;
  dot: string;
  /** Opens outside the app, in a new tab (the tester survey). */
  href?: string;
};

export function Menu({
  current = '',
  user,
  currentReport,
  assembling,
  reportCount,
}: {
  current?: MenuScreen;
  user: User | null;
  currentReport?: { id: string; address: string } | null;
  assembling?: { id: string; address: string } | null;
  reportCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const items: Item[] = [
    { label: 'New report', sub: 'Search an address', to: '/search', key: 'search', dot: c.red },
    {
      label: 'Portfolio',
      sub: reportCount != null ? `${reportCount} report${reportCount === 1 ? '' : 's'}` : 'Your reports',
      to: '/portfolio',
      key: 'portfolio',
      dot: c.violet,
    },
    ...(currentReport
      ? [
          {
            label: 'Current report',
            sub: currentReport.address,
            to: `/report/${currentReport.id}`,
            key: 'report' as const,
            dot: c.cyan,
          },
        ]
      : []),
    ...(assembling
      ? [
          {
            label: 'Assembling',
            sub: `In progress · ${assembling.address}`,
            to: `/assembling/${assembling.id}`,
            key: 'assembling' as const,
            dot: c.magenta,
          },
        ]
      : []),
    { label: 'Account', sub: user?.email ?? 'Plan and billing', to: '/account', key: 'account', dot: c.ink45 },
    {
      label: 'Give feedback',
      sub: isNative ? 'Tell us what to improve · about 5 minutes' : 'Tester survey · about 5 minutes',
      to: '',
      key: '',
      dot: c.magenta,
      href: surveyUrl(user),
    },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', color: c.ink }}>
      <button
        onClick={() => setOpen(true)}
        aria-label="Menu"
        aria-expanded={open}
        // Bigger dots in a hairline pill, so it reads as a button (testers missed it).
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, height: 36, minWidth: 52, padding: '0 12px', borderRadius: 999, border: `1px solid ${c.ink18}`, background: 'rgba(255,255,255,0.55)' }}
      >
        {[c.red, c.violet, c.cyan].map((dot) => (
          <span key={dot} style={{ width: 7, height: 7, borderRadius: '50%', background: dot }} />
        ))}
      </button>

      {/* Portalled to <body>: inside the page it was caught in whatever layer
          its header sat in, and the ring's hint drew over it. */}
      {open &&
        createPortal(
        <div
          onClick={() => setOpen(false)}
          className="rb-sheet-wrap"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            background: 'rgba(31,26,31,0.45)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            animation: 'fadeIn .2s ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rb-sheet"
            style={{
              width: 390,
              maxWidth: '100%',
              boxSizing: 'border-box',
              background: c.screen,
              borderRadius: '40px 40px 0 0',
              padding: '16px var(--gx) 40px',
              display: 'flex',
              flexDirection: 'column',
              gap: 22,
              maxHeight: '100vh',
              overflowY: 'auto',
              animation: 'menuUp .28s ease-out',
            }}
          >
            <div style={{ width: 36, height: 4, borderRadius: 999, background: 'rgba(31,26,31,0.15)', alignSelf: 'center' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Wordmark />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: `1px solid ${c.ink18}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: c.ink,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                  <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {items.map((it) => {
                const here = it.key === current;
                return (
                  <button
                    key={it.key + it.to}
                    className="rb-row"
                    onClick={() => {
                      setOpen(false);
                      if (it.href) openExternal(it.href);
                      else navigate(it.to);
                    }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '26px 1fr auto',
                      alignItems: 'center',
                      gap: 14,
                      padding: '15px 0',
                      borderTop: `1px solid ${c.hairlineList}`,
                      color: c.ink,
                      textAlign: 'left',
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: here ? c.redSoft : c.chip,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: it.dot }} />
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                      <span style={{ fontSize: 15, fontWeight: 500 }}>{it.label}</span>
                      <span
                        style={{
                          fontSize: 11,
                          color: c.ink5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {it.sub}
                      </span>
                    </span>
                    <span style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: c.red, whiteSpace: 'nowrap' }}>
                      {here ? 'YOU ARE HERE' : it.href ? 'OPENS ↗' : ''}
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 6,
                borderTop: `1px solid ${c.hairlineList}`,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 14, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{user?.name ?? '—'}</div>
                <div style={{ fontSize: 11, color: c.ink5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.email ?? ''}
                </div>
              </div>
              <button
                onClick={() => {
                  setOpen(false);
                  void signOut();
                }}
                style={{
                  marginTop: 14,
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  fontWeight: 600,
                  border: `1px solid ${c.redLine}`,
                  padding: '9px 14px',
                  borderRadius: 999,
                  color: c.red,
                  whiteSpace: 'nowrap',
                }}
              >
                SIGN OUT
              </button>
            </div>
          </div>
        </div>,
          document.body,
        )}
    </div>
  );
}

/** The tester survey, outside the app; tagged with who is answering. */
export const surveyUrl = (user: User | null) => {
  const q = new URLSearchParams();
  if (user) q.set('u', user.id);
  if (isNative) q.set('app', '1');
  const s = q.toString();
  return `/survey/${s ? `?${s}` : ''}`;
};
